import logger from '../utils/logger';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getUserId } from '../utils/utils';
import '../style/AdminStaffAvailability.css';

// Matches RAD_MODALITIES in radiology-information-system-backend/routes/scheduling.js
// and the `exam` values used for Radiology orders (see schedule.exam).
const MODALITY_OPTIONS = ['CT', 'MRI', 'X-Ray', 'US', 'DEXA'];

const HOUR_OPTIONS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '17:40'];

const formatHourLabel = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  return `${displayHour}:${String(m).padStart(2, '0')} ${period}`;
};

const toLocalDateString = (year, month, day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
const todayDateStr = () => {
  const d = new Date();
  return toLocalDateString(d.getFullYear(), d.getMonth(), d.getDate());
};

// Rendered by components/StaffAvailability.js at /availability. It was an Admin
// Dashboard tab until that gating turned out to be the reason techs were being
// given the admin role — which then hid them from the rad_tech table this very
// page queries.
//
// It reads/writes the SAME rad_tech_modalities / rad_tech_blocked_times
// tables Health Hub's identical admin page uses — RIS and HH share one
// physical database (see docker-compose.yaml), so a change here is
// immediately visible in HH's admin page and vice versa. On the RIS side,
// a blocked day also hard-blocks booking that modality via
// POST /scheduling/orderSchedule and shows a warning badge on the Schedule
// month calendar.
export default function AdminRadTechAvailability({ BASE_URL, currentUser, user }) {
  const [radTechs, setRadTechs] = useState([]);
  const [radTechId, setRadTechId] = useState(null);
  const [modalities, setModalities] = useState([]); // assigned modalities for the selected tech
  const [currentDate, setCurrentDate] = useState(new Date());
  const [blocks, setBlocks] = useState([]); // rad_tech_blocked_times rows for the month

  const [showBlockModal, setShowBlockModal] = useState(false);
  const [pendingDate, setPendingDate] = useState(null);
  const [fullDay, setFullDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');

  const [coverageDate, setCoverageDate] = useState(todayDateStr());
  const [coverage, setCoverage] = useState(null);

  // This page now lives outside the admin dashboard, so a rad tech can reach it
  // to manage their own calendar without being granted the admin role. Admins
  // keep full reach; everyone else is pinned to their own record. The server
  // enforces the same rule (canManageAvailabilityFor in routes/scheduling.js) —
  // this only stops the UI offering actions that would come back 403.
  const isAdmin = currentUser?.type === 'admin';
  const selfId = getUserId(currentUser);
  const canEditBlocks = isAdmin
    || (currentUser?.type === 'rad_tech' && String(radTechId) === String(selfId));
  // Modality assignment is a competency record ("who is signed off on MRI"),
  // not a scheduling preference, so it stays admin-only — a self-assigned
  // modality would also be the coverage that unblocks booking it.
  const canEditModalities = isAdmin;

  const getMonthDateRange = (date) => {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return {
      startDate: startOfMonth.toISOString().split('T')[0],
      endDate: endOfMonth.toISOString().split('T')[0],
    };
  };

  useEffect(() => {
    const fetchRadTechs = async () => {
      try {
        const response = await fetch(`${BASE_URL}/scheduling/radTechs`);
        if (!response.ok) {
          logger.error(`Error fetching rad techs: ${response.status} ${response.statusText}`);
          toast.error('Failed to load rad techs — see console for details');
          return;
        }
        const data = await response.json();
        const techs = Array.isArray(data) ? data : [];
        setRadTechs(techs);
        // A rad tech lands on their own calendar; everyone else on the first tech.
        const own = currentUser?.type === 'rad_tech'
          ? techs.find(t => String(t.id) === String(getUserId(currentUser)))
          : null;
        if (own) setRadTechId(own.id);
        else if (techs.length > 0) setRadTechId(techs[0].id);
      } catch (err) {
        logger.error('Error fetching rad techs:', err);
      }
    };
    fetchRadTechs();
  }, []);

  const fetchModalities = async () => {
    if (!radTechId) return;
    try {
      const response = await fetch(`${BASE_URL}/scheduling/radTechModalities?rad_tech_id=${radTechId}`);
      if (response.ok) {
        const data = await response.json();
        setModalities(data.map(m => m.modality));
      }
    } catch (err) {
      logger.error('Error fetching rad tech modalities:', err);
    }
  };

  const fetchBlocks = async () => {
    if (!radTechId) return;
    const { startDate, endDate } = getMonthDateRange(currentDate);
    try {
      const response = await fetch(
        `${BASE_URL}/scheduling/radTechBlockedTimes?rad_tech_id=${radTechId}&startDate=${startDate}&endDate=${endDate}`
      );
      if (response.ok) {
        const data = await response.json();
        setBlocks(data);
      }
    } catch (err) {
      logger.error('Error fetching rad tech blocked times:', err);
    }
  };

  const fetchCoverage = async (date) => {
    try {
      const response = await fetch(`${BASE_URL}/scheduling/radTechCoverage?date=${date}`);
      if (response.ok) {
        const data = await response.json();
        setCoverage(data);
      }
    } catch (err) {
      logger.error('Error fetching rad tech coverage:', err);
    }
  };

  useEffect(() => {
    fetchModalities();
  }, [radTechId]);

  useEffect(() => {
    fetchBlocks();
  }, [currentDate, radTechId]);

  useEffect(() => {
    fetchCoverage(coverageDate);
  }, [coverageDate]);

  const toggleModality = async (modality) => {
    if (!canEditModalities) return;
    const isAssigned = modalities.includes(modality);
    try {
      if (isAssigned) {
        const response = await fetch(
          `${BASE_URL}/scheduling/radTechModalities?rad_tech_id=${radTechId}&modality=${encodeURIComponent(modality)}&user=${user}&type=${currentUser?.type}`,
          { method: 'DELETE' }
        );
        if (response.ok) {
          setModalities(prev => prev.filter(m => m !== modality));
          toast.success(`Removed ${modality}`);
        }
      } else {
        const response = await fetch(`${BASE_URL}/scheduling/radTechModalities`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rad_tech_id: radTechId, modality, user, type: currentUser?.type }),
        });
        if (response.ok) {
          setModalities(prev => [...prev, modality]);
          toast.success(`Assigned ${modality}`);
        }
      }
      if (coverageDate) fetchCoverage(coverageDate);
    } catch (err) {
      logger.error('Error updating modality assignment:', err);
      toast.error('Error updating modality assignment');
    }
  };

  const handleDayClick = (day) => {
    if (!canEditBlocks) {
      toast.info('You can only change your own availability.');
      return;
    }
    const dateStr = toLocalDateString(currentDate.getFullYear(), currentDate.getMonth(), day);
    setPendingDate(dateStr);
    setFullDay(false);
    setStartTime('09:00');
    setEndTime('17:00');
    setReason('');
    setShowBlockModal(true);
  };

  const handleAddBlock = async () => {
    if (!fullDay && endTime <= startTime) {
      toast.error('End time must be after start time');
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/scheduling/radTechBlockedTimes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rad_tech_id: radTechId,
          blocked_date: pendingDate,
          full_day: fullDay,
          start_time: fullDay ? null : `${startTime}:00`,
          end_time: fullDay ? null : `${endTime}:00`,
          reason,
          user,
          type: currentUser?.type,
        }),
      });
      if (response.ok) {
        toast.success(`Blocked ${pendingDate}${fullDay ? ' (all day)' : ` from ${formatHourLabel(startTime)} to ${formatHourLabel(endTime)}`}`);
        setFullDay(false);
        setStartTime('09:00');
        setEndTime('17:00');
        setReason('');
        await fetchBlocks();
        if (pendingDate === coverageDate) fetchCoverage(coverageDate);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || 'Failed to block time');
      }
    } catch (err) {
      logger.error('Error blocking rad tech time:', err);
      toast.error('Error blocking rad tech time');
    }
  };

  const handleDeleteBlock = async (id) => {
    try {
      const response = await fetch(`${BASE_URL}/scheduling/radTechBlockedTimes?id=${id}&user=${user}&type=${currentUser?.type}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast.success('Blocked time removed');
        await fetchBlocks();
        fetchCoverage(coverageDate);
      } else {
        toast.error('Failed to remove blocked time');
      }
    } catch (err) {
      logger.error('Error removing rad tech blocked time:', err);
      toast.error('Error removing blocked time');
    }
  };

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="avail-calendar-day" style={{ visibility: 'hidden' }} />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = toLocalDateString(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dayBlocks = blocks.filter(b => b.blocked_date.split('T')[0] === dateStr);
    const isFullyBlocked = dayBlocks.some(b => !!b.full_day);
    const hasPartialBlock = dayBlocks.some(b => !b.full_day);
    const isPast = new Date(dateStr) < new Date(new Date().toDateString());
    days.push(
      <div
        key={day}
        className={`avail-calendar-day ${isFullyBlocked ? 'full-blocked' : (hasPartialBlock ? 'partial-blocked' : '')} ${isPast ? 'past' : ''}`}
        onClick={() => handleDayClick(day)}
        title={isFullyBlocked
          ? 'Unavailable all day'
          : (hasPartialBlock ? 'Partially unavailable' : (canEditBlocks ? 'Click to add a block' : 'Available'))}
      >
        <span className="avail-day-number">{day}</span>
        {isFullyBlocked && <span className="avail-day-badge full">Unavailable</span>}
        {!isFullyBlocked && hasPartialBlock && <span className="avail-day-badge partial">Limited</span>}
      </div>
    );
  }

  const upcomingBlocks = blocks
    .filter(b => new Date(b.blocked_date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.blocked_date) - new Date(b.blocked_date) || a.start_time.localeCompare(b.start_time));

  const blocksForPendingDate = pendingDate ? blocks.filter(b => b.blocked_date.split('T')[0] === pendingDate) : [];

  return (
    <div className="avail-container">
      <h1 className="avail-title">Rad Tech Availability</h1>
      <p className="avail-subtitle">
        Track which modalities each radiology technologist can operate, and block off hourly windows or full days when a
        tech is unavailable. A modality with no covering tech on a given day is blocked from being scheduled and flagged
        on the Schedule calendar.
      </p>

      <div className="avail-selector-row">
        <label htmlFor="radtech-select">Rad Tech:</label>
        <select
          id="radtech-select"
          className="avail-selector"
          value={radTechId || ''}
          onChange={(e) => setRadTechId(parseInt(e.target.value, 10))}
        >
          {radTechs.map(t => (
            <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>
          ))}
        </select>
      </div>

      <div className="avail-modality-section">
        <h3>Modalities This Tech Can Operate</h3>
        {!canEditModalities && (
          <p className="avail-subtitle">
            Modality sign-off is a credentialing record — only an admin can change it.
          </p>
        )}
        <div className="avail-modality-checkboxes">
          {MODALITY_OPTIONS.map(mod => (
            <label key={mod} className="avail-modality-checkbox">
              <input
                type="checkbox"
                checked={modalities.includes(mod)}
                disabled={!canEditModalities}
                onChange={() => toggleModality(mod)}
              />
              {mod}
            </label>
          ))}
        </div>
      </div>

      <div className="avail-two-col">
        <div className="avail-coverage-section">
          <h3>Daily Imaging Coverage</h3>
          <input
            type="date"
            className="avail-coverage-date-input"
            value={coverageDate}
            onChange={(e) => setCoverageDate(e.target.value)}
          />
          {coverage && (
            <table className="avail-coverage-table">
              <thead>
                <tr>
                  <th>Modality</th>
                  <th>Techs</th>
                </tr>
              </thead>
              <tbody>
                {MODALITY_OPTIONS.map(mod => {
                  const techs = coverage.modalities?.[mod] || [];
                  return (
                    <tr key={mod}>
                      <td>{mod}</td>
                      <td>
                        {techs.length === 0 && <span style={{ color: '#999' }}>No techs assigned</span>}
                        {techs.map(t => {
                          const status = t.fully_blocked ? 'unavailable' : (t.blocked_windows.length > 0 ? 'partial' : 'available');
                          const label = status === 'unavailable'
                            ? `${t.name} — Unavailable`
                            : status === 'partial'
                              ? `${t.name} — ${t.blocked_windows.map(w => `${formatHourLabel(w.start.slice(0, 5))}–${formatHourLabel(w.end.slice(0, 5))}`).join(', ')}`
                              : `${t.name} — Available`;
                          return (
                            <span key={t.rad_tech_id} className={`avail-tech-chip ${status}`}>{label}</span>
                          );
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {upcomingBlocks.length > 0 && (
          <div className="avail-upcoming-section">
            <h3>Upcoming Blocked Times</h3>
            <ul className="avail-upcoming-list">
              {upcomingBlocks.map(b => {
                const [year, month, day] = b.blocked_date.split('T')[0].split('-');
                const display = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10))
                  .toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                return (
                  <li key={b.id} className={`avail-upcoming-item ${b.full_day ? '' : 'partial'}`}>
                    <span className="avail-upcoming-date">
                      {display}{!b.full_day && ` — ${formatHourLabel(b.start_time.slice(0, 5))}–${formatHourLabel(b.end_time.slice(0, 5))}`}
                      {!!b.full_day && ' — All Day'}
                    </span>
                    {b.reason && <span className="avail-upcoming-reason">{b.reason}</span>}
                    {canEditBlocks && (
                      <button className="avail-remove-btn" onClick={() => handleDeleteBlock(b.id)}>Remove</button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <div className="avail-calendar-header">
        <button className="avail-nav-btn" onClick={prevMonth}>{'<'}</button>
        <h2>{currentDate.toLocaleString('default', { month: 'long' })} {currentDate.getFullYear()}</h2>
        <button className="avail-nav-btn" onClick={nextMonth}>{'>'}</button>
      </div>

      <div className="avail-calendar-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="avail-calendar-day-header">{d}</div>
        ))}
        {days}
      </div>

      {showBlockModal && (
        <div className="avail-modal-overlay" onClick={() => setShowBlockModal(false)}>
          <div className="avail-modal-box" onClick={e => e.stopPropagation()}>
            <h3>Block {pendingDate}</h3>

            {blocksForPendingDate.length > 0 && (
              <div className="avail-existing-blocks">
                <div className="avail-existing-blocks-title">Existing blocks for this day</div>
                {blocksForPendingDate.map(b => (
                  <div key={b.id} className="avail-existing-block-item">
                    <span>
                      {b.full_day ? 'All Day' : `${formatHourLabel(b.start_time.slice(0, 5))}–${formatHourLabel(b.end_time.slice(0, 5))}`}
                      {b.reason ? ` (${b.reason})` : ''}
                    </span>
                    <button className="avail-remove-btn" onClick={() => handleDeleteBlock(b.id)}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            <label className="avail-form-checkbox-label">
              <input type="checkbox" checked={fullDay} onChange={(e) => setFullDay(e.target.checked)} />
              Unavailable all day
            </label>

            {!fullDay && (
              <div className="avail-time-row">
                <label className="avail-form-label">
                  Start Time
                  <select className="avail-select" value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                    {HOUR_OPTIONS.map(h => <option key={h} value={h}>{formatHourLabel(h)}</option>)}
                  </select>
                </label>
                <label className="avail-form-label">
                  End Time
                  <select className="avail-select" value={endTime} onChange={(e) => setEndTime(e.target.value)}>
                    {HOUR_OPTIONS.map(h => <option key={h} value={h}>{formatHourLabel(h)}</option>)}
                  </select>
                </label>
              </div>
            )}

            <label className="avail-form-label">
              Reason (optional)
              <input
                type="text"
                className="avail-reason-input"
                placeholder="e.g. Training, PTO..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>

            <div className="avail-modal-buttons">
              <button className="avail-btn-confirm" onClick={handleAddBlock}>Add Block</button>
              <button className="avail-btn-cancel" onClick={() => setShowBlockModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
